// Side-effect import: this file used to import `useIsFreeUser`, which made that
// module (and its `useQueryUserBillingInfo` chain) initialize before
// `useIsAuthenticated` pulls in the auth/store chain. Dropping the named
// import reordered module init and tripped a circular-dependency TDZ ("Cannot
// access 'auth' before initialization") in Jest. Preload the same module to
// restore the prior order — importing `useIsFreeUser` (not the billing module
// directly) so existing test mocks of `@/hooks/useIsFreeUser` still apply. See SCR-4877.
// (The `selectedReviewMemberAtom` / `PERMISSION_SUBJECTS` /
// `useSessionPermissionCheck` imports that used to sit here moved to
// `@/hooks/useSessionAccessContext` with the composition; the preload stays
// because the auth edge below is still the chain it orders.)
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import '@/hooks/useIsFreeUser';
import {
  usePrefetchSessionById as usePrefetchSessionByIdCore,
  useQuerySessionByIdAuthenticated as useQuerySessionByIdAuthenticatedCore,
  useQueryTeamMemberSessionById as useQueryTeamMemberSessionByIdCore,
} from '@heidi/session/data';
import { useIsReviewingOtherUser, useQueryUser } from './useQueryUser';

// The session type closure lives in `@heidi/session/contracts` (OpenSpec
// `monorepo-session-data-core` C2); these two are what the delegate signatures
// below need.
import type {
  QuerySessionResponse,
  SelectQuerySessionResponse,
} from '@heidi/session/contracts';

// What is left here is the three signature-stable delegates below. Every other
// name this module re-exported now resolves at `@heidi/session/data` or
// `@heidi/session/contracts`; only the load-bearing side-effect preloads and
// the delegates' own importers still name this path.

/**
 * Signature-stable delegate (W3c): resolves the redux auth-token gate and the
 * reviewing-other-user read app-side and passes them into the package hook.
 */
export function useQuerySessionByIdAuthenticated<T = QuerySessionResponse | undefined>({
  sessionId,
  select,
  isExternalLaunch = false,
  hasSharedSessionEditPermission = false,
  refetchOnMount,
}: {
  sessionId?: string;
  select?: SelectQuerySessionResponse<T>;
  isExternalLaunch?: boolean;
  hasSharedSessionEditPermission?: boolean;
  refetchOnMount?: boolean | 'always';
}) {
  const { isAuthenticated } = useIsAuthenticated();
  const isReviewingOtherUser = useIsReviewingOtherUser();

  return useQuerySessionByIdAuthenticatedCore({
    sessionId,
    select,
    isExternalLaunch,
    hasSharedSessionEditPermission,
    refetchOnMount,
    isAuthenticated,
    isReviewingOtherUser,
  });
}

/**
 * Signature-stable delegate (W3c): resolves the redux auth-token gate and the
 * current user's kinde id app-side and passes them into the package hook.
 *
 * Fetches a team member's session by ID for Action Center (assistant view).
 * Uses v2 GET /teams/{teamId}/members/{memberId}/sessions/{sessionId} so assistants
 * can load any session in the team list; v1 session-by-id would 403 for non-owners.
 */
export function useQueryTeamMemberSessionById<T = QuerySessionResponse | undefined>({
  teamId,
  memberId,
  sessionId,
  select,
}: {
  teamId: string;
  memberId: string;
  sessionId: string;
  select?: SelectQuerySessionResponse<T>;
}) {
  const { isAuthenticated } = useIsAuthenticated();
  const { data: userData } = useQueryUser();

  return useQueryTeamMemberSessionByIdCore({
    teamId,
    memberId,
    sessionId,
    select,
    isAuthenticated,
    currentUserId: userData?.kinde_user_id,
  });
}

/**
 * Signature-stable delegate (W3c): resolves the reviewing-other-user cohort
 * read app-side and passes it into the package hook.
 */
export function usePrefetchSessionById() {
  const isReviewingOtherUser = useIsReviewingOtherUser();
  return usePrefetchSessionByIdCore({ isReviewingOtherUser });
}
